import React, { useState, useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell,
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../../../components/ui/table";
import { Input } from "../../../../components/ui/input";
import { PodMetricsMap } from '../utils/types';
import PodRow from './PodRow';
import { ArrowDown, ArrowUp, Search, Info } from 'lucide-react';
import { Badge } from "../../../../components/ui/badge";
import { parseMemory, bytesToGB } from '../utils/helpers';

interface PodTableProps {
  pods: any[];
  podMetrics: PodMetricsMap;
  podSessionMap?: Map<string, any[]>;
  isLoadingSessions?: boolean;
  isLoadingMetrics?: boolean;
  showDeployment?: boolean;
  showNode?: boolean;
  showApp?: boolean;
  showCpu?: boolean;
  showMemoryInfo?: boolean;
  emptyMessage?: string;
  maxHeight?: string;
  sortByMemory?: boolean;
  showSearch?: boolean;
}

const PodTable: React.FC<PodTableProps> = ({
  pods,
  podMetrics,
  podSessionMap = new Map(),
  isLoadingSessions = false,
  isLoadingMetrics = false,
  showDeployment = true,
  showNode = false,
  showApp = true,
  showCpu = false,
  showMemoryInfo = false,
  emptyMessage = "No pods found.",
  maxHeight = "auto",
  sortByMemory = false,
  showSearch = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Process and sort pods as needed
  const processedPods = useMemo(() => {
    if (isLoadingMetrics) return [];
    
    // Convert pods to objects with memory usage data
    const podsWithMetrics = pods.map(pod => {
      const podName = pod.metadata.name;
      const podMetric = podMetrics[podName];
      
      // Default to 0 if no metrics
      const memoryBytes = podMetric?.memory?.usageBytes || 0;
      const memoryGB = parseFloat(bytesToGB(memoryBytes));
      
      return {
        pod,
        memoryBytes,
        memoryGB
      };
    });
    
    // Filter by search term if provided
    const filteredPods = searchTerm.trim() ? 
      podsWithMetrics.filter(({ pod }) => {
        const term = searchTerm.toLowerCase();
        return (
          pod.metadata?.name?.toLowerCase().includes(term) ||
          pod.metadata?.namespace?.toLowerCase().includes(term) || 
          pod.metadata?.labels?.['app.kubernetes.io/name']?.toLowerCase().includes(term) ||
          pod.spec?.nodeName?.toLowerCase().includes(term)
        );
      }) : podsWithMetrics;
    
    // Sort by memory usage if requested
    if (sortByMemory) {
      filteredPods.sort((a, b) => {
        return sortDirection === 'desc' 
          ? b.memoryBytes - a.memoryBytes
          : a.memoryBytes - b.memoryBytes;
      });
    }
    
    return filteredPods;
  }, [pods, podMetrics, searchTerm, sortDirection, sortByMemory, isLoadingMetrics]);

  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  if (pods.length === 0) {
    return (
      <div className="py-4 text-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      {showSearch && (
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search pods..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      )}
      <div style={{ maxHeight, overflowY: maxHeight !== 'auto' ? 'auto' : 'visible' }}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sessions</TableHead>
              <TableHead>Pod ID</TableHead>
              <TableHead className="w-[300px]">Pod Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[150px]">
                <div 
                  className={`flex items-center ${sortByMemory ? 'cursor-pointer' : ''}`} 
                  onClick={sortByMemory ? toggleSortDirection : undefined}
                >
                  Memory 
                  {sortByMemory && (
                    sortDirection === 'desc' ? 
                      <ArrowDown className="ml-1 h-4 w-4" /> : 
                      <ArrowUp className="ml-1 h-4 w-4" />
                  )}
                </div>
              </TableHead>
              {showCpu && <TableHead>CPU</TableHead>}
              {showApp && <TableHead>App</TableHead>}
              <TableHead>Namespace</TableHead>
              {showDeployment && <TableHead>Deployment</TableHead>}
              {showNode && <TableHead>Node</TableHead>}
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingMetrics ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-4">
                  Loading pod metrics...
                </TableCell>
              </TableRow>
            ) : processedPods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-4">
                  No pods found matching your search.
                </TableCell>
              </TableRow>
            ) : (
              processedPods.map(({ pod }) => (
                <PodRow
                  key={pod.metadata.name}
                  pod={pod}
                  podMetrics={podMetrics}
                  podSessionMap={podSessionMap}
                  isLoadingSessions={isLoadingSessions}
                  showDeployment={showDeployment}
                  showNode={showNode}
                  showApp={showApp}
                  showCpu={showCpu}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {showMemoryInfo && (
        <div className="p-3 bg-muted/20 border-t text-sm">
          <div className="flex items-center space-x-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            <span>Memory usage thresholds: </span>
            <Badge variant="outline">Normal (&lt;3GB)</Badge>
            <Badge variant="secondary">Warning (&gt;3GB)</Badge>
            <Badge variant="destructive">Critical (&gt;4GB)</Badge>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodTable; 